const NETWORK_TYPES = new Set(['E', 'H', 'H+', 'G', 'LTE', '4G', '5G']);

export type NetworkType = 'E' | 'H' | 'H+' | 'G' | 'LTE' | '4G' | '5G';

export function normalizeNetworkType(value: unknown): NetworkType | null {
  if (value == null || value === '') {
    return null;
  }
  const normalized = String(value).trim().toUpperCase();
  return NETWORK_TYPES.has(normalized) ? (normalized as NetworkType) : null;
}

export function normalizeSignalStrength(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  const strength = Number(value);
  if (Number.isNaN(strength)) {
    return null;
  }
  return Math.max(0, Math.min(4, Math.round(strength)));
}

/**
 * Builds a cellular-style signal icon: network type label + 0–4 bars.
 * Reads signalStrength and networkType (falls back to lteSignalStrength if string).
 */
export function formatSignalIconHtml(
  source: {
    signalStrength?: unknown;
    networkType?: unknown;
    lteSignalStrength?: unknown;
  } | null | undefined,
  emptyLabel: string = 'N/A',
): string {
  if (!source) {
    return emptyLabel;
  }

  const bars = normalizeSignalStrength(source.signalStrength);
  const networkType =
    normalizeNetworkType(source.networkType) ??
    normalizeNetworkType(source.lteSignalStrength);

  if (bars == null && !networkType) {
    return emptyLabel;
  }

  const filledCount = bars ?? 0;
  const showNoSignalMark = bars == null || bars === 0;
  const barTitle =
    bars == null || bars === 0
      ? 'No signal'
      : bars === 1
        ? '1 bar'
        : `${bars} bars`;
  const title = [networkType, barTitle].filter(Boolean).join(' · ');

  const labelHtml = networkType
    ? `<span class="nw-signal-label">${networkType}</span>`
    : `<span class="nw-signal-label nw-signal-label-empty"></span>`;

  const noneHtml = showNoSignalMark
    ? `<span class="nw-signal-none" aria-hidden="true">×</span>`
    : '';

  const barsHtml = [1, 2, 3, 4]
    .map(
      (level) =>
        `<span class="nw-signal-bar${
          level <= filledCount && !showNoSignalMark ? ' filled' : ''
        }"></span>`,
    )
    .join('');

  return `<span class="nw-signal${
    showNoSignalMark ? ' nw-signal-none-state' : ''
  }" title="${title}">${labelHtml}<span class="nw-signal-bars-wrap">${noneHtml}<span class="nw-signal-bars" aria-hidden="true">${barsHtml}</span></span></span>`;
}
